import { sharedSheet } from "../styles/sharedStyles.js";
import componentStylesText from "./ExpandableBlockComponent.css?inline";

const componentSheet = new CSSStyleSheet();
componentSheet.replaceSync(componentStylesText);

/**
 * ExpandableBlockComponent — Reusable presentational shell for inline-expandable content.
 *
 * It provides the bubble chrome and a toggle button, but does not own the expanded state
 * or handle any animations. It is a controlled component.
 *
 * Slots:
 * - default: The content to be displayed inside the bubble.
 *
 * Attributes:
 * - `expand-button-label`: Label for the toggle button when collapsed.
 * - `collapse-button-label`: Label for the toggle button when expanded.
 * - `role`: Used for styling (e.g., "assistant").
 * - `expanded`: (Reflected) Presence indicates the block should be styled as expanded.
 *
 * Events:
 * - `toggle-request`: Fired when the button is clicked. 
 *    Detail: { expanded: boolean } (the requested next state)
 */
export class ExpandableBlockComponent extends HTMLElement {
    private toggleButton: HTMLButtonElement | null = null;
    private toggleLabel: HTMLElement | null = null;
    private toggleIcon: HTMLElement | null = null;

    static get observedAttributes(): string[] {
        return ["expand-button-label", "collapse-button-label", "expanded"];
    }

    constructor() {
        super();
    }

    connectedCallback(): void {
        if (!this.shadowRoot) {
            this.render();
        }
        
        this.updateLabels();
        this.updateIcon();
        this.updateAria();
    }

    attributeChangedCallback(name: string, _oldValue: string | null, _newValue: string | null): void {
        if (!this.shadowRoot) {
            return;
        }
        
        if (name === "expand-button-label" || name === "collapse-button-label" || name === "expanded") {
            this.updateLabels();
            this.updateIcon();
            this.updateAria();
        }
    }

    public get expanded(): boolean {
        return this.hasAttribute("expanded");
    }

    public set expanded(value: boolean) {
        if (value) {
            this.setAttribute("expanded", "");
        } else {
            this.removeAttribute("expanded");
        }
    }

    private requestToggle(): void {
        this.dispatchEvent(new CustomEvent("toggle-request", {
            bubbles: true,
            composed: true,
            detail: { expanded: !this.expanded }
        }));
    }

    private updateLabels(): void {
        if (this.toggleLabel) {
            const labelAttr = this.expanded ? "collapse-button-label" : "expand-button-label";
            this.toggleLabel.textContent = this.getAttribute(labelAttr) || "";
        }
    }

    private updateIcon(): void {
        if (this.toggleIcon) {
            const icon = this.expanded ? "nav-arrow-up" : "nav-arrow-down";
            this.toggleIcon.setAttribute("icon", icon);
        }
    }

    private updateAria(): void {
        if (this.toggleButton) {
            this.toggleButton.setAttribute("aria-expanded", this.expanded.toString());
        }
    }

    private render(): void {
        const shadow = this.attachShadow({ mode: "open" });
        shadow.adoptedStyleSheets = [sharedSheet, componentSheet];

        const container = document.createElement("div");
        container.className = "expandable-block";

        const content = document.createElement("div");
        content.className = "chat-message__bubble expandable-block__content";
        
        const slot = document.createElement("slot");
        content.appendChild(slot);

        container.appendChild(content);

        const button = document.createElement("button");
        button.type = "button";
        button.className = "expandable-block__toggle";
        button.addEventListener("click", () => this.requestToggle());

        const label = document.createElement("span");
        label.className = "expandable-block__toggle-label";
        this.toggleLabel = label;

        const icon = document.createElement("jt-icon");
        this.toggleIcon = icon;

        button.appendChild(label);
        button.appendChild(icon);
        container.appendChild(button);

        this.toggleButton = button;
        shadow.appendChild(container);
    }
}

// Register custom element
if (!customElements.get("jt-expandable-block")) {
    customElements.define("jt-expandable-block", ExpandableBlockComponent);
}

